/**
 * Kanban Column Component
 * 
 * Column container for task cards with drag-and-drop support
 */

import { TaskBoardColumn } from '@/types/tasks';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TaskCard } from './task-card';
import { UnifiedTask, TaskStatus } from '@/types/tasks';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { useState } from 'react';

interface KanbanColumnProps {
  column: TaskBoardColumn;
  onTaskStatusChange: (taskId: string, newStatus: TaskStatus) => void;
  onViewTaskDetails?: (task: UnifiedTask) => void;
  onDragStart?: (task: UnifiedTask) => void;
  onDragEnd?: () => void;
  onDrop?: (status: TaskStatus) => void;
  isDragging?: boolean;
}

const columnColors = {
  todo: 'bg-gray-100 dark:bg-gray-800',
  in_progress: 'bg-blue-100 dark:bg-blue-900',
  blocked: 'bg-yellow-100 dark:bg-yellow-900',
  completed: 'bg-green-100 dark:bg-green-900',
};

export function KanbanColumn({ 
  column, 
  onTaskStatusChange, 
  onViewTaskDetails,
  onDragStart,
  onDragEnd,
  onDrop,
  isDragging,
}: KanbanColumnProps) {
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    onDrop?.(column.id as TaskStatus);
  };

  return (
    <div className="flex flex-col h-full min-w-[320px]">
      <Card 
        className={cn(
          "flex flex-col h-full transition-all",
          isDragOver && "ring-2 ring-primary ring-offset-2"
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold">
              {column.title}
            </CardTitle>
            <Badge 
              variant="secondary" 
              className={columnColors[column.id]}
            >
              {column.tasks.length}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="flex-1 overflow-hidden p-0">
          <ScrollArea className="h-full px-4 pb-4">
            <div className="space-y-3 min-h-[200px]">
              {column.tasks.length === 0 ? (
                <div className="text-center py-8 text-sm text-muted-foreground">
                  {isDragging ? 'Drop here' : 'No tasks'}
                </div>
              ) : (
                column.tasks.map(task => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    onStatusChange={onTaskStatusChange}
                    onViewDetails={onViewTaskDetails}
                    onDragStart={onDragStart}
                    onDragEnd={onDragEnd}
                  />
                ))
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
